import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ArrowLeft, Download, HelpCircle, Plus, Trash2 } from 'lucide-react';

interface Transaction {
  id: string;
  date: string;
  type: 'buy' | 'sell';
  shares: number;
  pricePerShare: number;
  fees: number;
}

interface LotCalculation {
  purchaseDate: string;
  shares: number;
  costBasis: number;
  costPerShare: number;
  remainingShares: number;
}

export default function CostBasisCalculator() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [newTransaction, setNewTransaction] = useState<Omit<Transaction, 'id'>>({
    date: new Date().toISOString().split('T')[0],
    type: 'buy',
    shares: 0,
    pricePerShare: 0,
    fees: 0
  });

  const addTransaction = () => {
    if (newTransaction.shares <= 0 || newTransaction.pricePerShare <= 0) {
      return;
    }

    const transaction: Transaction = {
      ...newTransaction,
      id: `txn-${Date.now()}`
    };

    setTransactions([...transactions, transaction]);
    setNewTransaction({
      date: new Date().toISOString().split('T')[0],
      type: 'buy',
      shares: 0,
      pricePerShare: 0,
      fees: 0
    });
    setShowAddForm(false);
  };

  const removeTransaction = (id: string) => {
    setTransactions(transactions.filter(t => t.id !== id));
  };

  const calculateLots = (): LotCalculation[] => {
    const lots: LotCalculation[] = [];
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    for (const transaction of sortedTransactions) {
      if (transaction.type === 'buy') {
        const totalCost = (transaction.shares * transaction.pricePerShare) + transaction.fees;
        lots.push({
          purchaseDate: transaction.date,
          shares: transaction.shares,
          costBasis: totalCost,
          costPerShare: totalCost / transaction.shares,
          remainingShares: transaction.shares
        });
      } else {
        // Handle sell transactions using FIFO method
        let sharesToSell = transaction.shares;
        for (const lot of lots) {
          if (sharesToSell <= 0) break;
          if (lot.remainingShares > 0) {
            const sharesSold = Math.min(sharesToSell, lot.remainingShares);
            lot.remainingShares -= sharesSold;
            sharesToSell -= sharesSold;
          }
        }
      }
    }

    return lots;
  };

  const calculateTotalShares = (): number => {
    return transactions.reduce((total, t) => 
      total + (t.type === 'buy' ? t.shares : -t.shares), 
    0);
  };

  const calculateAverageCostBasis = (): number => {
    const lots = calculateLots();
    const totalRemainingShares = lots.reduce((sum, lot) => sum + lot.remainingShares, 0);
    const totalRemainingCost = lots.reduce((sum, lot) => 
      sum + (lot.remainingShares * lot.costPerShare), 
    0);

    return totalRemainingShares > 0 ? totalRemainingCost / totalRemainingShares : 0;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const downloadCalculations = () => {
    const lots = calculateLots();
    let csv = 'Cost Basis Calculations\n\n';
    
    // Transactions
    csv += 'Transactions\n';
    csv += 'Date,Type,Shares,Price Per Share,Fees,Total\n';
    transactions.forEach(t => {
      csv += `${t.date},${t.type},${t.shares},${t.pricePerShare},${t.fees},${
        t.shares * t.pricePerShare + t.fees
      }\n`;
    });

    // Lots
    csv += '\nLots\n';
    csv += 'Purchase Date,Original Shares,Cost Basis,Cost Per Share,Remaining Shares\n';
    lots.forEach(lot => {
      csv += `${lot.purchaseDate},${lot.shares},${lot.costBasis},${lot.costPerShare},${lot.remainingShares}\n`;
    });

    // Summary
    csv += '\nSummary\n';
    csv += `Total Shares,${calculateTotalShares()}\n`;
    csv += `Average Cost Basis,${calculateAverageCostBasis()}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cost-basis-calculations.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Cost Basis Calculator
              </h1>
              <p className="text-gray-600">
                Calculate your investment cost basis and track gains/losses
              </p>
            </div>
            <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-brand-700" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Transactions Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Transactions</h2>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowInfo(!showInfo)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <HelpCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-800 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Transaction
                  </button>
                </div>
              </div>

              {showInfo && (
                <div className="mb-6 p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-medium text-brand-900 mb-2">About Cost Basis</h3>
                  <p className="text-sm text-brand-800">
                    Cost basis is the original value of an investment for tax purposes, adjusted for stock splits, dividends, and return of capital distributions. This calculator uses the First-In-First-Out (FIFO) method for calculating cost basis.
                  </p>
                </div>
              )}

              {showAddForm && (
                <div className="mb-6 p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add Transaction</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={newTransaction.date}
                        onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transaction Type
                      </label>
                      <select
                        value={newTransaction.type}
                        onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value as 'buy' | 'sell' })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      >
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Shares
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        value={newTransaction.shares || ''}
                        onChange={(e) => setNewTransaction({ ...newTransaction, shares: Number(e.target.value) })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price per Share
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newTransaction.pricePerShare || ''}
                          onChange={(e) => setNewTransaction({ ...newTransaction, pricePerShare: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fees
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newTransaction.fees || ''}
                          onChange={(e) => setNewTransaction({ ...newTransaction, fees: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addTransaction}
                      className="px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-800 transition-colors"
                    >
                      Add Transaction
                    </button>
                  </div>
                </div>
              )}

              {/* Transactions List */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium">Shares</th>
                      <th className="pb-3 font-medium">Price/Share</th>
                      <th className="pb-3 font-medium">Fees</th>
                      <th className="pb-3 font-medium">Total</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-gray-100">
                        <td className="py-3">{new Date(transaction.date).toLocaleDateString()}</td>
                        <td className="py-3 capitalize">{transaction.type}</td>
                        <td className="py-3">{transaction.shares.toFixed(4)}</td>
                        <td className="py-3">{formatCurrency(transaction.pricePerShare)}</td>
                        <td className="py-3">{formatCurrency(transaction.fees)}</td>
                        <td className="py-3">
                          {formatCurrency(transaction.shares * transaction.pricePerShare + transaction.fees)}
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => removeTransaction(transaction.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {transactions.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No transactions added yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
                <button
                  onClick={downloadCalculations}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                  disabled={transactions.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Shares</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {calculateTotalShares().toFixed(4)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Average Cost Basis</p>
                  <p className="text-2xl font-bold text-brand-700">
                    {formatCurrency(calculateAverageCostBasis())}
                  </p>
                  <p className="text-sm text-gray-500">per share</p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Remaining Lots</h3>
                  <div className="space-y-2">
                    {calculateLots()
                      .filter(lot => lot.remainingShares > 0)
                      .map((lot, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {new Date(lot.purchaseDate).toLocaleDateString()}
                          </span>
                          <span className="font-medium text-gray-900">
                            {lot.remainingShares.toFixed(4)} shares @ {formatCurrency(lot.costPerShare)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-brand-50 rounded-xl p-6">
              <h3 className="font-semibold text-brand-900 mb-2">About FIFO Method</h3>
              <p className="text-sm text-brand-800 mb-4">
                This calculator uses the First-In-First-Out (FIFO) method, where the oldest shares are sold first. This is one of several methods allowed for calculating cost basis:
              </p>
              <ul className="text-sm text-brand-800 space-y-2">
                <li>• Shares are sold in the order they were purchased</li>
                <li>• Each lot maintains its own cost basis</li>
                <li>• Useful for tax loss harvesting strategies</li>
                <li>• Consider consulting a tax professional for advice</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}